// src/components/module/ventas/SelectorImpresion.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import { impresorasService, TipoImpresion, TipoImpresora, Impresora } from '../../../services/api/ptint/PrintService';
import { PDFViewer } from '@react-pdf/renderer';
import CloseIcon from '@mui/icons-material/Close';
import { FacturaCompletaPDF } from './facturas/FacturaCompletaPDF';
import { TicketPOS } from './facturas/TicketPOS';
import { DetalleVenta } from '../../../components/types/ventas.types';
import { useConfiguracionEmpresa } from '../../../hooks/useConfiguracionEmpresa';

interface SelectorImpresionProps {
  open: boolean;
  onClose: () => void;
  onImprimir: (opciones: OpcionesImpresion) => Promise<void>;
  sucursalId: number;
  venta: DetalleVenta; 
}

interface OpcionesImpresion {
  tipo: TipoImpresion;
  impresora: TipoImpresora;
  nombreImpresora?: string;
  configuracionEmpresa: {
    nombre: string;
    nit: string;
    direccion: string;
    telefono: string;
    email: string;
    sitioWeb?: string;
  };
}

export const SelectorImpresion: React.FC<SelectorImpresionProps> = ({
  open,
  onClose,
  onImprimir,
  venta,
  sucursalId
}) => {
  const { empresa } = useConfiguracionEmpresa();
  const [impresoras, setImpresoras] = useState<Impresora[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [opciones, setOpciones] = useState<Partial<OpcionesImpresion>>({
    tipo: TipoImpresion.FACTURA_COMPLETA
  });

  // Modificar el useEffect para que dependa de 'open'
  useEffect(() => {
    if (open) {
      cargarImpresoras();
    } else {
      // Limpiar estado cuando se cierra el modal
      setImpresoras([]);
      setError(null);
    }
  }, [open]); // Agregar 'open' como dependencia

  const cargarImpresoras = async () => {
    try {
      setLoading(true);
      const impresorasDisponibles = await impresorasService.obtenerImpresoras();
      setImpresoras(impresorasDisponibles);
      setError(null);
    } catch (error) {
      setError('No se pudieron cargar las impresoras');
      setImpresoras([]);
    } finally {
      setLoading(false);
    }
  };

  const impresorasFiltradas = useMemo(() => {
    if (!opciones.tipo) return [];
    return impresorasService.filtrarImpresorasPorTipo(impresoras, opciones.tipo);
  }, [impresoras, opciones.tipo]);

  const handleChange = (field: keyof OpcionesImpresion, value: any) => {
    setOpciones(prev => ({
      ...prev,
      [field]: value
    }));
    if (error) setError(null);
  };

  const handleSubmit = async () => {
    if (!opciones.tipo || !opciones.impresora) {
      setError('Por favor, complete todos los campos');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onImprimir(opciones as OpcionesImpresion);
      onClose();
    } catch (error) {
      setError('Error al enviar a imprimir. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const renderPreview = () => { 
    
    const DocumentoPDF = opciones.tipo === TipoImpresion.FACTURA_COMPLETA
      ? FacturaCompletaPDF
      : TicketPOS;
  
    return (
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Vista Previa</DialogTitle>
        <DialogContent sx={{ height: '80vh', p: 0 }}>
          <PDFViewer width="100%" height="100%" style={{ border: 'none' }}>
            <DocumentoPDF
              venta={venta}
              configuracionEmpresa={empresa}
            />
          </PDFViewer>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Opciones de Impresión</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Tipo de Impresión</InputLabel>
              <Select
                value={opciones.tipo || ''}
                label="Tipo de Impresión"
                onChange={(e) => handleChange('tipo', e.target.value)}
                disabled={loading}
              >
                <MenuItem value={TipoImpresion.FACTURA_COMPLETA}>
                  Factura Completa
                </MenuItem>
                <MenuItem value={TipoImpresion.TICKET_POS}>
                  Ticket POS
                </MenuItem>
              </Select>
            </FormControl>

            {/* ... resto de los controles ... */}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>
            Cancelar
          </Button>
          <Tooltip title="Vista previa">
            <Button
              onClick={() => setShowPreview(true)}
              disabled={!opciones.tipo}
              startIcon={<PreviewIcon />}
            >
              Vista Previa
            </Button>
          </Tooltip>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !opciones.tipo || !opciones.nombreImpresora}
          >
            {loading ? 'Cargando...' : 'Imprimir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de vista previa */}
      {showPreview && renderPreview()}
    </>
  );
};